import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GradientDots } from '@/components/ui/GradientDots';
import { OperatorWordmark } from '@/components/auth/OperatorWordmark';

interface LegalPageProps {
  /** Document heading, e.g. "Terms of Service". */
  title: string;
  /** Human-readable effective date shown under the title. */
  effectiveDate: string;
  /** Section blocks rendered as the document body. */
  children: ReactNode;
}

/**
 * Public shell for the legal documents (Terms, Privacy). Mirrors the auth
 * surface's identity — GradientDots backdrop, glass card, operator wordmark —
 * so /terms and /privacy feel like part of the same product when reached from
 * the sign-in footer. The page scrolls naturally for long content.
 */
export function LegalPage({ title, effectiveDate, children }: LegalPageProps) {
  return (
    <div
      className="relative min-h-screen px-4 py-12 sm:px-6 lg:px-8"
      style={{ backgroundColor: 'hsl(var(--auth-bg))' }}
    >
      <GradientDots />

      <div className="relative z-10 mx-auto w-full max-w-3xl">
        <div className="rounded-[32px] border border-foreground/15 bg-card/95 p-6 shadow-[0_80px_160px_-30px_rgba(0,0,0,0.8),0_40px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-3xl dark:bg-card/15 sm:p-12 md:p-16">
          <div className="flex flex-col items-center text-center">
            <Link to="/signin" aria-label="Back to sign in">
              <OperatorWordmark size="md" />
            </Link>
            <h2 className="mt-8 text-2xl font-semibold text-foreground sm:text-3xl">
              {title}
            </h2>
            <p className="mt-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Effective {effectiveDate}
            </p>
          </div>

          <div className="mx-auto mt-10 max-w-prose space-y-8 text-sm leading-relaxed text-muted-foreground [&_a]:text-primary-600 [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-primary-500 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground [&_li]:ml-1 [&_p+p]:mt-3 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
            {children}
          </div>

          <div className="mt-12 border-t border-foreground/10 pt-6 text-center">
            <Link
              to="/signin"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/** A titled section within a legal document. */
export function LegalSection({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h3>{heading}</h3>
      {children}
    </section>
  );
}
